import { DoorOpen, LogIn, LogOut, Plus, Send, Shuffle, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { BreakoutRoom, BreakoutSetup, BreakoutState, Participant } from '../../shared/protocol';
import { Avatar, Button } from '../components/ui';
import { addRoom, assignRandom, makeRooms, suggestedRooms, type AssignMode } from '../lib/breakout';
import { useStore } from '../lib/store';
import type { MeetingSession } from '../meeting/MeetingSession';

/** Seconds left until `closesAt`, refreshed every second. */
export function useCountdown(closesAt: number | null): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!closesAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [closesAt]);
  return closesAt ? Math.max(0, Math.ceil((closesAt - now) / 1000)) : 0;
}

const MAIN = '__main__';

function setupOf(b: BreakoutState, patch: Partial<BreakoutSetup> = {}): BreakoutSetup {
  return { rooms: b.rooms, assignments: b.assignments, allowReturn: b.allowReturn, allowChoose: b.allowChoose, countdownSeconds: b.countdownSeconds, ...patch };
}

/**
 * Host's breakout rooms panel (Zoom's "Breakout Rooms" window): create rooms, assign people at
 * random or one by one, open, move people while open, message every room, close.
 * Every change goes to the server at once, so the plan is the same for every host device.
 */
export function BreakoutPanel({ session, onClose }: { session: MeetingSession; onClose(): void }) {
  const s = useStore(session.store);
  const b = s.breakout;
  const selfId = s.selfId!;
  const everyone = Object.values(s.everyone).sort((x, y) => x.joinedAt - y.joinedAt);
  const guests = everyone.filter((p) => p.role !== 'host');
  const [count, setCount] = useState(() => suggestedRooms(guests.length));
  const [mode, setMode] = useState<AssignMode>('random');
  const [message, setMessage] = useState('');
  const left = useCountdown(b.closesAt);
  const live = b.status !== 'idle';
  const myRoom = s.everyone[selfId]?.breakoutRoomId ?? null;

  const send = (patch: Partial<BreakoutSetup>) => session.breakoutSetup(setupOf(b, patch));

  const create = () => {
    const rooms = makeRooms(count);
    session.breakoutSetup(
      setupOf(b, {
        rooms,
        assignments: mode === 'random' ? assignRandom(guests.map((p) => p.id), rooms) : {},
        allowChoose: mode === 'choose' || b.allowChoose,
      }),
    );
  };

  /** Where someone is (open) or is planned to go (prepared). */
  const placeOf = (p: Participant): string => (live ? (p.breakoutRoomId ?? MAIN) : (b.assignments[p.id] ?? MAIN));

  const moveTo = (p: Participant, roomId: string) => {
    const target = roomId === MAIN ? null : roomId;
    if (live) return session.breakoutMove(p.id, target);
    const assignments = { ...b.assignments };
    if (target) assignments[p.id] = target;
    else delete assignments[p.id];
    send({ assignments });
  };

  const rename = (room: BreakoutRoom, name: string) => {
    const clean = name.trim();
    if (!clean || clean === room.name) return;
    send({ rooms: b.rooms.map((r) => (r.id === room.id ? { ...r, name: clean } : r)) });
  };

  const removeRoom = (room: BreakoutRoom) => {
    const assignments = Object.fromEntries(Object.entries(b.assignments).filter(([, rid]) => rid !== room.id));
    send({ rooms: b.rooms.filter((r) => r.id !== room.id), assignments });
  };

  const groups = [
    ...b.rooms.map((r) => ({ id: r.id, room: r as BreakoutRoom | null, people: everyone.filter((p) => placeOf(p) === r.id) })),
    {
      id: MAIN,
      room: null,
      people: everyone.filter((p) => placeOf(p) === MAIN && (live || p.role !== 'host')),
    },
  ];

  return (
    <section className="panel breakout-panel" aria-label="Salas para grupos pequeños">
      <div className="panel-header">
        <span>Salas para grupos pequeños</span>
        <button type="button" className="icon-btn" aria-label="Cerrar salas para grupos pequeños" onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      {b.rooms.length === 0 ? (
        <div className="panel-body bo-create">
          <p className="hint">Divide la reunión en grupos. Cada sala tiene su propio audio, vídeo, chat y pantalla compartida.</p>
          <label className="field">
            <span>Número de salas</span>
            <input className="input" type="number" min={1} max={50} value={count} onChange={(e) => setCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))} aria-label="Número de salas" />
          </label>
          <p className="hint">
            {guests.length === 0
              ? 'Aún no hay participantes: puedes preparar las salas ahora y repartir a la gente cuando entre.'
              : `${guests.length} ${guests.length === 1 ? 'participante' : 'participantes'} · unas ${Math.max(1, Math.ceil(guests.length / count))} por sala`}
          </p>
          <div className="bo-modes" role="radiogroup" aria-label="Cómo asignar">
            {(
              [
                ['random', 'Al azar', 'Nexo reparte a todos en grupos equilibrados.'],
                ['manual', 'Elijo yo', 'Asignas a cada persona a la sala que quieras.'],
                ['choose', 'Que elijan', 'Cada participante entra en la sala que prefiera.'],
              ] as const
            ).map(([id, title, text]) => (
              <label key={id} className="bo-mode">
                <input type="radio" name="bo-mode" checked={mode === id} onChange={() => setMode(id)} />
                <span>
                  <strong>{title}</strong>
                  <small>{text}</small>
                </span>
              </label>
            ))}
          </div>
          <Button variant="primary" block onClick={create}>
            Crear salas
          </Button>
        </div>
      ) : (
        <>
          <div className="panel-body">
            <div className={`bo-status ${b.status}`} role="status">
              {b.status === 'idle' && 'Salas preparadas: aún no están abiertas'}
              {b.status === 'open' && 'Salas abiertas'}
              {b.status === 'closing' && `Las salas se cierran en ${left} s`}
            </div>

            {groups.map((g) => (
              <div key={g.id} className="bo-room" data-room={g.room?.name ?? 'principal'}>
                <div className="bo-room-head">
                  {g.room ? (
                    <input
                      key={g.room.name}
                      className="bo-room-name"
                      defaultValue={g.room.name}
                      aria-label={`Nombre de ${g.room.name}`}
                      maxLength={40}
                      onBlur={(e) => rename(g.room!, e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                    />
                  ) : (
                    <strong className="bo-room-name">{live ? 'Sesión principal' : 'Sin asignar'}</strong>
                  )}
                  <span className="bo-count">{g.people.length}</span>
                  {g.room && live && myRoom !== g.room.id && (
                    <Button size="sm" variant="ghost" onClick={() => session.breakoutGo(g.room!.id)} aria-label={`Unirse a ${g.room.name}`}>
                      <LogIn size={14} /> Unirse
                    </Button>
                  )}
                  {g.room && b.status !== 'closing' && (
                    <button type="button" className="icon-btn" aria-label={`Eliminar ${g.room.name}`} title="Eliminar sala" onClick={() => removeRoom(g.room!)}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
                {g.people.length === 0 && <p className="hint bo-empty">{g.room ? 'Nadie todavía' : live ? 'Nadie en la sesión principal' : 'Todos tienen sala'}</p>}
                {g.people.map((p) => (
                  <div key={p.id} className="plist-row bo-person">
                    <Avatar name={p.name} seed={p.id} size={26} />
                    <div className="plist-name">
                      <strong>
                        {p.name}
                        {p.id === selfId ? ' (Yo)' : ''}
                      </strong>
                    </div>
                    {p.id !== selfId && (
                      <select className="select bo-move" aria-label={`Sala de ${p.name}`} value={placeOf(p)} onChange={(e) => moveTo(p, e.target.value)} disabled={b.status === 'closing'}>
                        {b.rooms.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                        <option value={MAIN}>{live ? 'Sesión principal' : 'Sin asignar'}</option>
                      </select>
                    )}
                  </div>
                ))}
              </div>
            ))}

            {b.status !== 'closing' && (
              <div className="bo-tools">
                {!live && (
                  <Button size="sm" variant="ghost" onClick={() => send({ assignments: assignRandom(guests.map((p) => p.id), b.rooms) })}>
                    <Shuffle size={14} /> Repartir al azar
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => send({ rooms: addRoom(b.rooms) })} disabled={b.rooms.length >= 50}>
                  <Plus size={14} /> Añadir sala
                </Button>
              </div>
            )}

            <div className="bo-options">
              <label className="check">
                <input type="checkbox" checked={b.allowChoose} onChange={(e) => send({ allowChoose: e.target.checked })} />
                Los participantes pueden elegir sala
              </label>
              <label className="check">
                <input type="checkbox" checked={b.allowReturn} onChange={(e) => send({ allowReturn: e.target.checked })} />
                Pueden volver a la sesión principal cuando quieran
              </label>
              <label className="field bo-countdown">
                <span>Cuenta atrás al cerrar</span>
                <select className="select" value={b.countdownSeconds} onChange={(e) => send({ countdownSeconds: Number(e.target.value) })}>
                  {[0, 15, 30, 60, 120].map((n) => (
                    <option key={n} value={n}>
                      {n === 0 ? 'Cerrar al momento' : `${n} segundos`}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {live && (
              <form
                className="bo-broadcast"
                onSubmit={(e) => {
                  e.preventDefault();
                  session.breakoutBroadcast(message);
                  setMessage('');
                }}
              >
                <label className="label" htmlFor="bo-broadcast">
                  Mensaje para todas las salas
                </label>
                <div className="row">
                  <input id="bo-broadcast" className="input" value={message} maxLength={500} placeholder="Por ejemplo: quedan 5 minutos" onChange={(e) => setMessage(e.target.value)} />
                  <Button type="submit" variant="secondary" disabled={!message.trim()} aria-label="Enviar a todas las salas">
                    <Send size={15} />
                  </Button>
                </div>
              </form>
            )}
          </div>

          <div className="panel-footer">
            {b.status === 'idle' && (
              <>
                <Button variant="ghost" onClick={() => session.breakoutSetup(setupOf(b, { rooms: [], assignments: {} }))}>
                  Eliminar salas
                </Button>
                <Button variant="primary" onClick={() => session.breakoutOpen()}>
                  <DoorOpen size={16} /> Abrir salas
                </Button>
              </>
            )}
            {b.status === 'open' && (
              <>
                {myRoom && (
                  <Button variant="ghost" onClick={() => session.breakoutGo(null)}>
                    <LogOut size={16} /> Volver a la principal
                  </Button>
                )}
                <Button variant="danger" onClick={() => session.breakoutClose()}>
                  Cerrar todas las salas
                </Button>
              </>
            )}
            {b.status === 'closing' && (
              <Button variant="danger" onClick={() => session.breakoutClose(true)}>
                Cerrar ahora
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Bar on top of the stage while breakout rooms are running: which room you are in, ask for help,
 * leave; or, from the main session, join your room or pick one.
 */
export function BreakoutBar({ session }: { session: MeetingSession }) {
  const s = useStore(session.store);
  const b = s.breakout;
  const left = useCountdown(b.closesAt);
  const me = s.everyone[s.selfId!];
  const [pick, setPick] = useState('');
  if (!me || b.status === 'idle') return null;
  const amHost = me.role === 'host';
  const room = b.rooms.find((r) => r.id === me.breakoutRoomId) ?? null;
  const assigned = b.rooms.find((r) => r.id === b.assignments[me.id]) ?? null;
  const countIn = (id: string) => Object.values(s.everyone).filter((p) => p.breakoutRoomId === id).length;

  if (room) {
    return (
      <div className="breakout-bar" role="toolbar" aria-label="Sala para grupos pequeños">
        <span className="bo-here" role="status">
          {room.name}
          {b.status === 'closing' && <span className="share-tag">se cierra en {left} s</span>}
        </span>
        {!amHost && (
          <button type="button" className="share-bar-btn" onClick={() => session.breakoutHelp()}>
            Pedir ayuda
          </button>
        )}
        {(amHost || b.allowReturn || b.status === 'closing') && (
          <button type="button" className="share-bar-btn" onClick={() => session.breakoutGo(null)}>
            <LogOut size={15} /> {b.status === 'closing' ? 'Volver ahora' : 'Salir de la sala'}
          </button>
        )}
      </div>
    );
  }

  // In the main session.
  if (amHost || b.status === 'closing') return null;
  if (b.allowChoose) {
    const chosen = pick || assigned?.id || b.rooms[0]?.id || '';
    return (
      <div className="breakout-bar" role="toolbar" aria-label="Salas para grupos pequeños">
        <span className="bo-here">Las salas están abiertas</span>
        <select className="select bo-pick" aria-label="Elegir sala" value={chosen} onChange={(e) => setPick(e.target.value)}>
          {b.rooms.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} ({countIn(r.id)})
            </option>
          ))}
        </select>
        <button type="button" className="share-bar-btn primary" onClick={() => chosen && session.breakoutGo(chosen)}>
          <LogIn size={15} /> Unirse
        </button>
      </div>
    );
  }
  if (assigned) {
    return (
      <div className="breakout-bar" role="toolbar" aria-label="Salas para grupos pequeños">
        <span className="bo-here">Te han asignado a {assigned.name}</span>
        <button type="button" className="share-bar-btn primary" onClick={() => session.breakoutGo(assigned.id)}>
          <LogIn size={15} /> Unirse
        </button>
      </div>
    );
  }
  return null;
}
